import { useTranslation } from "react-i18next"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"

interface ShipmentsSectionProps {
  /**
   * OKS-984 (UX Spec §6) — the page focuses this heading after a row leaves
   * the section, because the row's own trigger has been destroyed and focus
   * would otherwise fall to `<body>`.
   */
  headingRef?: React.RefObject<HTMLHeadingElement | null>
  /** Stable id — also the testid suffix and the focus target after a row leaves. */
  id: "ready" | "waiting" | "recently-shipped"
  title: string
  count: number
  /**
   * OKS-991 — the count AS A LABELLED PHRASE (« 11 colis · 25 articles »,
   * « 11 articles »), replacing the bare `({count})`.
   *
   * ⚠️ PRD §3 requires every count on this page to name its basis. The bare
   * parenthetical was a SECOND, unlabelled number beside the labelled heading
   * — PO's walk found « 11 colis · 25 articles (11) », where the trailing 11
   * silently means parcels and nothing on screen says so. Required rather
   * than optional: a section that forgets it reverts to the unlabelled form,
   * which is the defect.
   *
   * The phrase still lives INSIDE the `<h2>`, so a screen reader announces
   * "Prêt à expédier — 11 colis · 25 articles" as one accessible name — the
   * property the parenthetical existed for is kept, only its wording changes.
   */
  countLabel: string
  /** Shown inside the Card when `count` is 0 and the page is not wholly empty. */
  emptyLine: string
  children: React.ReactNode
  footer?: React.ReactNode
}

/**
 * OKS-984 — one Card section of the Shipments page (UX Spec §2.1).
 *
 * # ⚠️ The heading carries the count
 *
 * `<h2>Ready to ship (3)</h2>` rather than a heading plus a separate chip, so
 * a screen-reader user hears the count as part of the section name instead of
 * encountering an unattached number (UX Spec §6).
 *
 * # Focus target
 *
 * The heading is focusable via `tabIndex={-1}` AND the page actually moves
 * focus here — see `headingRef` and the row-count effect in
 * `pages/Brand/Shipments/Index.tsx`.
 *
 * ⚠️ `tabIndex={-1}` alone is a focus *target*, not a focus *move*. An earlier
 * revision had the attribute and this comment and no `.focus()` call anywhere,
 * so a keyboard or screen-reader operator dropped to `<body>` after shipping
 * while the comment said otherwise. A comment describing behaviour is not the
 * behaviour.
 *
 * # Per-section empty vs page empty
 *
 * A single muted line, NOT a full `EmptyState` — that is reserved for the
 * whole page being empty. A big illustrated empty state inside one populated
 * page reads as an error (UX Spec §3).
 */
export function ShipmentsSection({
  id,
  title,
  count,
  countLabel,
  emptyLine,
  children,
  footer,
  headingRef,
}: ShipmentsSectionProps) {
  return (
    <Card data-testid={`shipments-${id}-section`}>
      <CardHeader>
        {/* ⚠️ `CardTitle` is a plain `div` in this codebase (Base UI port),
            so the heading is nested rather than swapped in via `asChild`.
            The `<h2>` is what matters: the section must be a real heading in
            the document outline, with its count inside so screen readers
            announce "Ready to ship, 3" as one name. */}
        <CardTitle>
          <h2
            ref={headingRef}
            id={`shipments-${id}-heading`}
            tabIndex={-1}
            className="scroll-mt-24 text-base"
          >
            {title} — {countLabel}
          </h2>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {count === 0 ? (
          <p
            className="text-muted-foreground text-sm"
            data-testid={`shipments-${id}-empty`}
          >
            {emptyLine}
          </p>
        ) : (
          children
        )}
        {footer}
      </CardContent>
    </Card>
  )
}

/**
 * Skeleton rows matching the populated row geometry — avatar circle, two text
 * bars, a four-line address block, a button-sized bar.
 *
 * ⚠️ Never a spinner (UX Spec §3). A spinner says "something is happening"; a
 * skeleton says "content of this shape is arriving", which is what stops the
 * layout jumping and what tells the operator whether they are on the right
 * page.
 */
export function ShipmentsSectionSkeleton() {
  const { t } = useTranslation()

  return (
    <div
      className="flex flex-col gap-4"
      aria-label={t("shipments.loading")}
      data-testid="shipments-skeleton"
    >
      {[0, 1, 2].map((row) => (
        <div key={row} className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Skeleton className="size-8 rounded-full" />
            <Skeleton className="h-4 w-32" />
          </div>
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-24 w-full rounded-xl" />
          <Skeleton className="h-8 w-28" />
        </div>
      ))}
    </div>
  )
}
